import { useQuery, useMutation } from "@tanstack/react-query";
import { QueryModule } from "@/data/queryModules";
import { MutationEffectsOptions, useMutationEffects } from "@/data/useMutationEffects";
import { AppQueryOptions, AppMutationOptions } from "@povio/openapi-codegen-cli";
import { GuestProfileModels } from "./guestProfile.models";
import { GuestProfileApi } from "./guestProfile.api";

export namespace GuestProfileQueries {

export const moduleName = QueryModule.GuestProfile;

export const keys = {
    all: [moduleName] as const,
    getCurrent: () => [...keys.all, "/guest-profile", ] as const,
};




 /** 
 * Mutation `useRequestPresignedUrl`
 * @param { GuestProfileModels.GuestProfilePresignedUrlRequestDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<CommonModels.MediaUploadInstructions> } 
 * @statusCodes [201, 401, default]
 */
export const useRequestPresignedUrl = (options?: AppMutationOptions<typeof GuestProfileApi.requestPresignedUrl, { data: GuestProfileModels.GuestProfilePresignedUrlRequestDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            GuestProfileApi.requestPresignedUrl(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useSubmitResume`
 * @param { GuestProfileModels.GuestProfileResumeSubmitRequestDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [201, 401]
 */
export const useSubmitResume = (options?: AppMutationOptions<typeof GuestProfileApi.submitResume, { data: GuestProfileModels.GuestProfileResumeSubmitRequestDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            GuestProfileApi.submitResume(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useSubmitManual`
 * @param { GuestProfileModels.GuestProfileManualSubmitRequestDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [201, 401]
 */
export const useSubmitManual = (options?: AppMutationOptions<typeof GuestProfileApi.submitManual, { data: GuestProfileModels.GuestProfileManualSubmitRequestDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            GuestProfileApi.submitManual(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





 /** 
 * Query `useGetCurrent`
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<GuestProfileModels.GuestProfileResponseDto> } 
 * @statusCodes [200, 401]
 */
export const useGetCurrent = <TData>(options?: AppQueryOptions<typeof GuestProfileApi.getCurrent, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getCurrent(),
    queryFn: GuestProfileApi.getCurrent
    ,
    ...options,
  });
};








 /** 
 * Mutation `useClear`
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [200, 401]
 */
export const useClear = (options?: AppMutationOptions<typeof GuestProfileApi.clear, {  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: () => 
      
            GuestProfileApi.clear()
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useShareByEmail`
 * @param { GuestProfileModels.GuestProfileShareRequestDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [201, 401]
 */
export const useShareByEmail = (options?: AppMutationOptions<typeof GuestProfileApi.shareByEmail, { data: GuestProfileModels.GuestProfileShareRequestDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { data } ) => 
      
            GuestProfileApi.shareByEmail(data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useEdit`
 * @param { string } mutation.id Path parameter
 * @param { GuestProfileModels.GuestProfileEditRequestDto } mutation.data Body parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<GuestProfileModels.GuestProfileResponseDto> } 
 * @statusCodes [200, 401, default]
 */
export const useEdit = (options?: AppMutationOptions<typeof GuestProfileApi.edit, { id: string, data: GuestProfileModels.GuestProfileEditRequestDto,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id, data } ) => 
      
            GuestProfileApi.edit(id, data)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      
      const updateKeys = [keys.getCurrent(), ];
      await runMutationEffects(resData, options, updateKeys);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};







 /** 
 * Mutation `useTriggerWorthCalculation`
 * @param { string } mutation.id Path parameter
 * @param { AppMutationOptions & MutationEffectsOptions } options Mutation options
 * @returns { UseMutationResult<void> } 
 * @statusCodes [204, 401]
 */
export const useTriggerWorthCalculation = (options?: AppMutationOptions<typeof GuestProfileApi.triggerWorthCalculation, { id: string,  }> & MutationEffectsOptions) => {
  
  const { runMutationEffects } = useMutationEffects({ currentModule: moduleName });

  return useMutation({
    mutationFn: ( { id } ) => 
      
            GuestProfileApi.triggerWorthCalculation(id)
    ,
    ...options, 
    onSuccess: async (resData, variables, onMutateResult, context) => {
      await runMutationEffects(resData, options);
      options?.onSuccess?.(resData, variables, onMutateResult, context);
    },
  });
};





}
